import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { SudoButton } from "../../../sudokit/components/SudoButton";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor";
import { AnchorIds, TourSignal } from "../../StickyTour/TourTypes";
import { NewProjectCardContainer } from "./styles";

const actions = [
  { id: "blank", label: "Blank Project", icon: "/assets/project.svg", handler: "onCreateBlank" },
  { id: "generate", label: "Generate Project", icon: "/assets/AI-sparkle.svg", handler: "onGenerate" },
  { id: "import", label: "Import Project", icon: "/assets/import-icon.svg", handler: "onImport" },
  { id: "folder", label: "Folder", icon: "/assets/folder.svg", handler: "onCreateFolder" },
  { id: "series", label: "Series", icon: "/assets/series.svg", handler: "onCreateSeries" },
];

const actionListVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.02,
      staggerChildren: 0.055,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.025,
      staggerDirection: -1,
    },
  },
};

const actionVariants = {
  hidden: { opacity: 0, x: -16, y: 16, scale: 0.9 },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 520, damping: 29, mass: 0.75 },
  },
  exit: { opacity: 0, x: -8, y: 8, scale: 0.96, transition: { duration: 0.1 } },
};

export default function NewProjectCard({
  onCreateBlank,
  onGenerate,
  onImport,
  onCreateFolder,
  onCreateSeries,
  importDisabled = false,
  importDisabledReason,
  isInsideSeries = false,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const handlers = { onCreateBlank, onGenerate, onImport, onCreateFolder, onCreateSeries };
  const availableActions = isInsideSeries
    ? actions.filter((action) => action.id !== "folder" && action.id !== "series")
    : actions;

  useEffect(() => {
    if (!open) return;

    const closeOnOutsidePointer = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const runAction = (action) => {
    setOpen(false);
    handlers[action.handler]?.();
  };

  return (
    <NewProjectCardContainer
      ref={containerRef}
      className={open ? "open" : undefined}
      data-testid="new-project-card"
    >
      <AnimatePresence initial={false}>
        {!open ? (
          <motion.button
            key="trigger"
            type="button"
            className="new-project-card-trigger use-component-styles"
            aria-label="Create new"
            aria-expanded={false}
            onClick={() => {
              setOpen(true);
              sendFlowSignal(TourSignal.NEW_PROJECT_BUTTON_CLICKED);
            }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.12 }}
          >
            {isInsideSeries && (
              <span
                className="new-project-card-tour-anchor"
                aria-hidden="true"
              >
                <StickyNoteAnchor anchorId={AnchorIds.NEW_PROJECT_BUTTON} />
              </span>
            )}
            <SVG
              src="/assets/add.svg"
              className="add-icon"
              width={25}
              height={25}
              aria-hidden="true"
            />
            <span>New</span>
          </motion.button>
        ) : (
          <motion.div
            key="actions"
            className="new-project-actions"
            role="menu"
            aria-label="Create new"
            variants={actionListVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {availableActions.map((action) => {
              const disabled = action.id === "import" && importDisabled;
              return (
                <motion.div
                  key={action.id}
                  variants={actionVariants}
                >
                  <SudoButton
                    type="button"
                    className="new-project-action"
                    variant="tertiaryGrey"
                    size="default"
                    leftIcon={action.icon}
                    iconSize={20}
                    role="menuitem"
                    disabled={disabled}
                    title={disabled ? importDisabledReason : undefined}
                    onClick={() => runAction(action)}
                  >
                    {action.label}
                  </SudoButton>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </NewProjectCardContainer>
  );
}
